// GT Kreatív Sarok — "Irány 5" design tokens for MUI.
// Drop-in replacement for frontend/src/theme/index.ts
//
// Palette: ink #14161F, action red #E8402A (text-safe variant #C7351F),
// neutral surface #F4F3F1, category accents teal/mustard/plum/green/blue.
// Type: Figtree everywhere (400–900). Shape: 13px cards, 10px buttons.
//
// Add to frontend/public/index.html:
// <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
// <link href="https://fonts.googleapis.com/css2?family=Figtree:wght@400;500;600;700;800;900&display=swap" rel="stylesheet">

import { createTheme } from '@mui/material/styles';
import { huHU as materialHuHU } from '@mui/material/locale';
import { huGridLocalization } from './gridLocale';

export const gt = {
  ink: '#14161F',
  surface: '#F4F3F1',
  white: '#FFFFFF',
  red: '#E8402A',        // fills only (badges, primary buttons)
  redText: '#C7351F',    // red text on white — 5.5:1, WCAG AA
  teal: '#0E6E6E',
  mustard: '#F2A93B',
  mustardText: '#8A5A08', // mustard-as-text (stars, links on white)
  plum: '#7B2D5E',
  leaf: '#2F7346',
  blue: '#2E5BD8',
  grey: '#6B6F7D',
  line: '#E4DFD6',
  // category tile tints used behind product illustrations
  tint: {
    mug: '#FDE7E3',
    tee: '#E3F0EC',
    tote: '#FCF1DC',
  },
} as const;

const theme = createTheme({
  palette: {
    mode: 'light',
    primary:   { main: gt.red, light: '#F26A57', dark: '#C7351F', contrastText: '#fff' },
    secondary: { main: gt.teal, light: '#3C9292', dark: '#095050', contrastText: '#fff' },
    success:   { main: gt.leaf, light: '#4F9A6A', dark: '#1F5531' },
    error:     { main: '#C7351F', light: '#E06550', dark: '#9A2614' },
    warning:   { main: gt.mustard, light: '#F7C471', dark: gt.mustardText },
    info:      { main: gt.blue, light: '#5F82E4', dark: '#1F41A5' },
    background: { default: gt.white, paper: gt.white },
    text: { primary: gt.ink, secondary: gt.grey },
    divider: gt.line,
  },
  typography: {
    fontFamily: '"Figtree", system-ui, sans-serif',
    h1: { fontWeight: 800, fontSize: 'clamp(1.5rem,3.1vw,2.375rem)', lineHeight: 1.05, letterSpacing: '-0.03em' },
    h2: { fontWeight: 800, fontSize: 'clamp(1.3rem,2.5vw,1.95rem)', lineHeight: 1.05, letterSpacing: '-0.025em' },
    h3: { fontWeight: 800, fontSize: '1.4rem', lineHeight: 1.15, letterSpacing: '-0.02em' },
    h4: { fontWeight: 800, fontSize: '1.125rem', lineHeight: 1.2, letterSpacing: '-0.02em' },
    h5: { fontWeight: 700, fontSize: '1rem', lineHeight: 1.25 },
    h6: { fontWeight: 700, fontSize: '0.9rem', lineHeight: 1.25 },
    body1: { fontSize: '0.9375rem', lineHeight: 1.5 },
    body2: { fontSize: '0.875rem', lineHeight: 1.5, color: gt.grey },
    button: { fontWeight: 800, textTransform: 'none', fontSize: '0.9rem', letterSpacing: 0 },
    overline: { fontSize: '0.72rem', fontWeight: 800, letterSpacing: '0.09em', textTransform: 'uppercase', lineHeight: 1.5 },
  },
  shape: { borderRadius: 13 },
  breakpoints: { values: { xs: 0, sm: 600, md: 900, lg: 1180, xl: 1440 } },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: { backgroundColor: gt.white, WebkitFontSmoothing: 'antialiased' },
        // tabular figures for every price / counter
        '.num, .MuiTypography-root[data-num]': { fontVariantNumeric: 'tabular-nums' },
      },
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: { borderRadius: 10, padding: '13px 24px', fontWeight: 800, borderWidth: 2, transition: '.18s' },
        contained: { backgroundColor: gt.red, color: '#fff', '&:hover': { backgroundColor: '#CF3521' } },
        outlined: {
          borderColor: gt.ink, color: gt.ink, borderWidth: 2,
          '&:hover': { backgroundColor: gt.ink, color: '#fff', borderColor: gt.ink, borderWidth: 2 },
        },
        text: { color: gt.redText, '&:hover': { backgroundColor: gt.surface } },
        sizeSmall: { padding: '10px 16px', fontSize: '0.8125rem', borderRadius: 9 },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 13, border: `1px solid ${gt.line}`, boxShadow: 'none',
          transition: '.2s', backgroundImage: 'none',
          '&:hover': { borderColor: gt.ink, boxShadow: '0 14px 28px rgba(20,22,31,.12)', transform: 'translateY(-3px)' },
        },
      },
    },
    MuiPaper: { styleOverrides: { root: { backgroundImage: 'none' }, rounded: { borderRadius: 13 } } },
    MuiTextField: {
      defaultProps: { variant: 'outlined' },
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 9, backgroundColor: gt.white,
            '& fieldset': { borderColor: gt.line, borderWidth: 1.5 },
            '&:hover fieldset': { borderColor: gt.ink },
            '&.Mui-focused fieldset': { borderColor: gt.ink, borderWidth: 1.5 },
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: { borderRadius: 999, fontWeight: 600, fontSize: '0.8125rem', height: 32 },
        filled: { backgroundColor: gt.surface, color: gt.ink },
        filledPrimary: { backgroundColor: gt.red, color: '#fff' },
        outlined: { borderColor: gt.line, borderWidth: 1.5, color: gt.ink },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: { backgroundImage: 'none', boxShadow: 'none', backgroundColor: gt.white, borderBottom: `1px solid ${gt.line}`, color: gt.ink },
      },
    },
    MuiDivider: { styleOverrides: { root: { borderColor: gt.line } } },
    MuiLink: { defaultProps: { underline: 'hover' }, styleOverrides: { root: { color: gt.ink, fontWeight: 600 } } },
    MuiTableCell: { styleOverrides: { root: { borderColor: gt.line }, head: { fontWeight: 800, fontSize: '0.72rem', letterSpacing: '0.08em', textTransform: 'uppercase', color: gt.grey } } },
  },
},
// Magyar feliratok: MUI komponensek (lapozó, Autocomplete „Nincs találat” stb.)
// és minden @mui/x-data-grid (Sorok/oldal, Nincs sor, „1–15 / 15”…).
materialHuHU, huGridLocalization);

export default theme;
